import './style.scss'
import { message, Modal, Popover, Radio } from 'antd'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { UserStore } from '@/global-states'
import { eventTracking } from '@/libs/util'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { CreateVideoStore } from '../../create-video-state'

interface IProps {
  openPlan: () => void
}

const CreateVideoSetting: FC<IProps> = (props) => {
  const { t: translate } = useTranslation(['create_video'])
  const { highSetting, model } = CreateVideoStore
  const { userPackage } = UserStore
  const { openPlan } = props

  const onDriveModelChange = (e: any) => {
    const value = e.target.value
    if (userPackage && (userPackage.current_membership_level || 0) < 20 && value === 1) {
      return Modal.confirm({
        title: translate('create_video.membership_level_not_enough'),
        content: <div>{translate('create_video.drive_mode_membership_content')}</div>,
        okText: translate('create_video.upgrade_membership'),
        cancelText: translate('create_video.cancel'),
        onOk: () => {
          openPlan()
        }
      })
    }
    CreateVideoStore.highSetting = {
      ...highSetting,
      drive_mode: e.target.value
    }
  }

  const onAudioModelChange = (e: any) => {
    const value = e.target.value
    if (highSetting.mode === 1 && value) {
      return message.warning(translate('create_video.dark_mode_audio_warning'))
    }
    if (userPackage && (userPackage.current_membership_level || 0) < 20 && value) {
      return Modal.confirm({
        title: translate('create_video.membership_level_not_enough'),
        content: <div>{translate('create_video.audio_only_membership_content')}</div>,
        okText: translate('create_video.upgrade_membership'),
        cancelText: translate('create_video.cancel'),
        onOk: () => {
          openPlan()
        }
      })
    }
    CreateVideoStore.highSetting = {
      ...highSetting,
      only_generate_audio: value
    }
  }

  const onModeChange = (e: any) => {
    const value = e.target.value
    if (highSetting.only_generate_audio && value === 1) {
      return message.warning(translate('create_video.dark_mode_audio_warning'))
    }
    if (userPackage && (userPackage.current_membership_level || 0) < 50 && value === 1) {
      return Modal.confirm({
        title: translate('create_video.black_membership_exclusive'),
        content: <div>{translate('create_video.dark_mode_membership_content')}</div>,
        okText: translate('create_video.open_black_membership'),
        cancelText: translate('create_video.cancel'),
        onOk: () => {
          openPlan()
        }
      })
    }
    CreateVideoStore.highSetting = {
      ...highSetting,
      mode: value
    }
  }

  const onModelVersionChange = (e: any) => {
    const value = e.target.value
    if (userPackage && (userPackage.current_membership_level || 0) < 10 && value === 1) {
      eventTracking('ModelVersionClick')
      return Modal.confirm({
        title: translate('create_video.membership_exclusive'),
        content: <div>{translate('create_video.model_version_membership_content')}</div>,
        okText: translate('create_video.open_membership'),
        cancelText: translate('create_video.cancel'),
        onOk: () => {
          openPlan()
        }
      })
    }
    CreateVideoStore.highSetting = {
      ...highSetting,
      model_version: value
    }
  }

  return (
    <div className="create-video-setting">
      <div>
        <div className="high-header">
          <label>{translate('create_video.advanced_options')}</label>
        </div>
        <div className="high-setting">
          <div className="setting-item">
            <div className="title">
              {translate('create_video.drive_mode')}
              <Popover
                content={
                  <div className="common-popover">
                    <div>{translate('create_video.drive_mode_popover_title')}</div>
                    <ul>
                      <li>{translate('create_video.sequential_drive_description')}</li>
                      <li>{translate('create_video.random_mode_description')}</li>
                    </ul>
                  </div>
                }
                placement="right"
              >
                <ExclamationCircleOutlined />
              </Popover>
            </div>
            <Radio.Group onChange={onDriveModelChange} value={highSetting.drive_mode}>
              <Radio value={0}>{translate('create_video.random_mode')}</Radio>
              <Radio value={1}>{translate('create_video.sequential_drive')}</Radio>
            </Radio.Group>
          </div>

          {model === 'text' && (
            <div className="setting-item">
              <div className="title">
                {translate('create_video.output_settings')}
                <Popover
                  content={
                    <div className="common-popover">
                      <div>{translate('create_video.output_settings_popover_title')}</div>
                      <ul>
                        <li>{translate('create_video.drive_video_description')}</li>
                        <li>
                          {translate('create_video.audio_only_description')}{' '}
                          <a
                            style={{ textDecoration: 'underline' }}
                            target="_blank"
                            href="https://lingverse.feishu.cn/sheets/IJeQsahvdhqX5btBSzycBKPRnr0"
                            rel="noreferrer"
                          >
                            {translate('create_video.points_explanation')}
                          </a>
                        </li>
                      </ul>
                    </div>
                  }
                  placement="right"
                >
                  <ExclamationCircleOutlined />
                </Popover>
              </div>
              <Radio.Group onChange={onAudioModelChange} value={highSetting.only_generate_audio}>
                <Radio value={false}>{translate('create_video.drive_video')}</Radio>
                <Radio value={true}>{translate('create_video.audio_only')}</Radio>
              </Radio.Group>
            </div>
          )}

          <div className="setting-item">
            <div className="title">
              {translate('create_video.generation_mode')}
              <Popover
                content={
                  <div className="common-popover">
                    <ul>
                      <li>
                        <a
                          target="_blank"
                          href="https://lingverse.feishu.cn/docx/VxnydIE4ao5NCyx7fnycqPX7npd#share-Evkidf4qYoA4AfxzoZJclzRkndh"
                          rel="noreferrer"
                          style={{ paddingLeft: 2, textDecoration: 'underline' }}
                        >
                          {translate('create_video.dark_mode')}
                        </a>
                        {translate('create_video.dark_mode_description')}
                      </li>
                      <li>{translate('create_video.points_mode_description')}</li>
                    </ul>
                  </div>
                }
                placement="right"
              >
                <ExclamationCircleOutlined />
              </Popover>
            </div>
            <Radio.Group onChange={onModeChange} value={highSetting.mode}>
              <Radio value={0}>{translate('create_video.points_mode')}</Radio>
              <Radio value={1}>{translate('create_video.dark_mode')}</Radio>
            </Radio.Group>
          </div>

          <div className="setting-item">
            <div className="title">
              {translate('create_video.select_model')}
              <Popover
                content={
                  <div className="common-popover">
                    <ul>
                      <li>{translate('create_video.model_version_description')}</li>
                    </ul>
                  </div>
                }
                placement="right"
              >
                <ExclamationCircleOutlined />
              </Popover>
            </div>
            <Radio.Group onChange={onModelVersionChange} value={highSetting.model_version}>
              <Radio value={0}>V1.0</Radio>
              <Radio value={1}>V1.5</Radio>
            </Radio.Group>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CreateVideoSetting
